import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Loader, Empty, Pagination } from "@/components/Page";
import BattleReplay from "@/components/BattleReplay";

export function Leaderboard({ war }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    loadLeaderboard();
  }, [war]);
  
  const loadLeaderboard = async () => {
    try {
      const res = await api.get("/alliance-wars/leaderboard");
      setData(res.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) return <Loader label="Tallying scores…" />;
  if (!data) return null;
  
  const { current_round, total_rounds, rankings, mvp_race } = data;
  
  return (
    <div className="space-y-6">
      {/* Alliance Rankings */}
      <div className="border border-amber-900/30 bg-zinc-950/50 overflow-hidden rounded-sm">
        <div className="bg-zinc-900/50 px-6 py-4 border-b border-amber-900/30">
          <h3 className="font-cinzel text-xs uppercase tracking-wide text-amber-500">
            Alliance Rankings
          </h3>
          <div className="text-[10px] text-zinc-600 mt-1 uppercase tracking-widest font-manrope">
            {current_round} of {total_rounds} rounds complete
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-900/30 text-[10px] text-zinc-600 uppercase tracking-widest font-manrope">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">Rank</th>
                <th className="px-6 py-3 text-left font-semibold">Alliance</th>
                {[...Array(7)].map((_, i) => (
                  <th key={i} className="px-3 py-3 text-center font-semibold">R{i + 1}</th>
                ))}
                <th className="px-6 py-3 text-center font-bold text-amber-500">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rankings.map((alliance, idx) => {
                return (
                  <tr key={alliance.alliance_id} className="hover:bg-zinc-900/20 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm text-zinc-300 font-cinzel">{alliance.rank}</span>
                    </td>
                    <td className="px-6 py-4">
                      <Link 
                        to={`/alliance/${alliance.alliance_id}`}
                        className="font-cinzel text-sm text-amber-400 hover:text-amber-300 uppercase tracking-wide transition-colors"
                      >
                        {alliance.tag && <span className="text-zinc-500">[{alliance.tag}]</span>} {alliance.name}
                      </Link>
                    </td>
                    {[...Array(7)].map((_, i) => {
                      const round = (i + 1).toString();
                      const points = alliance.round_points?.[round];
                      
                      return (
                        <td key={i} className="px-3 py-4 text-center">
                          {points !== undefined ? (
                            <span className="text-zinc-300 text-sm font-mono">{points}</span>
                          ) : (
                            <span className="text-zinc-700">-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-6 py-4 text-center">
                      <span className="font-cinzel text-base text-amber-400 font-bold">
                        {alliance.total_points}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* MVP Race */}
      {mvp_race && mvp_race.length > 0 && (
        <div className="border border-amber-900/30 bg-zinc-950/50 overflow-hidden rounded-sm">
          <div className="bg-zinc-900/50 px-6 py-4 border-b border-amber-900/30">
            <h3 className="font-cinzel text-xs uppercase tracking-wide text-amber-500">
              MVP Race
            </h3>
            <div className="text-[10px] text-zinc-600 mt-1 uppercase tracking-widest font-manrope">
              Most combined wins across a Lanista's submitted fighters
            </div>
          </div>
          
          <div className="divide-y divide-zinc-800">
            {mvp_race.slice(0, 10).map((lanista, idx) => (
              <div
                key={lanista.user_id}
                className="px-6 py-4 flex items-center justify-between hover:bg-zinc-900/20 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`font-cinzel text-lg ${
                    idx === 0 ? "text-amber-400" : "text-zinc-500"
                  }`}>
                    #{idx + 1}
                  </div>
                  <div>
                    <Link 
                      to={`/profile/${lanista.user_id}`}
                      className="text-sm text-amber-400 hover:text-amber-300 font-cinzel uppercase tracking-wide transition-colors"
                    >
                      {lanista.lanista_name}
                    </Link>
                    <div className="text-[10px] text-zinc-400 font-manrope mt-0.5">
                      <Link 
                        to={`/profile/${lanista.user_id}`}
                        className="hover:text-amber-400 transition-colors"
                      >
                        Lanista
                      </Link>
                      <span className="text-zinc-600"> • </span>
                      <span className="text-zinc-500">{lanista.alliance}</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="font-cinzel text-base text-amber-400">{lanista.total_wins}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-manrope">wins</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function FightResults({ war }) {
  // Default to last completed round (not the current/upcoming round)
  const getLastCompletedRound = () => {
    // If war is completed, default to round 7 (the final round)
    if (war.status === "completed") return 7;
    const currentRound = war.current_round || 0;
    // If current_round is 3, last completed is 2
    // If current_round is 1, no rounds completed yet, default to 1
    return currentRound > 1 ? currentRound - 1 : 1;
  };
  
  const [selectedRound, setSelectedRound] = useState(getLastCompletedRound());
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const FIGHTS_PER_PAGE = 20;
  
  useEffect(() => {
    if (selectedRound) {
      loadRoundResults(selectedRound);
    }
  }, [selectedRound]);
  
  // Reset to page 1 when changing rounds
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRound]);
  
  const loadRoundResults = async (round) => {
    setLoading(true);
    try {
      const res = await api.get(`/alliance-wars/round/${round}/fights`);
      setResults(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setResults(null);
      } else {
        toast.error(apiErr(e));
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate pagination
  const totalFights = results?.fights?.length || 0;
  const totalPages = Math.ceil(totalFights / FIGHTS_PER_PAGE);
  const startIndex = (currentPage - 1) * FIGHTS_PER_PAGE;
  const endIndex = startIndex + FIGHTS_PER_PAGE;
  const paginatedFights = results?.fights?.slice(startIndex, endIndex) || [];
  
  return (
    <div className="space-y-6">
      {/* Round Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[...Array(7)].map((_, i) => {
          const round = i + 1;
          // A round is completed if war is completed OR if round < current_round
          const isCompleted = war.status === "completed" || round < war.current_round;
          const isCurrent = round === selectedRound;
          
          return (
            <button
              key={round}
              onClick={() => setSelectedRound(round)}
              disabled={!isCompleted && round !== war.current_round}
              className={`px-4 py-2 font-cinzel uppercase text-sm tracking-wide whitespace-nowrap transition border ${
                isCurrent
                  ? "bg-amber-900/20 text-amber-300 border-amber-700/50"
                  : isCompleted
                  ? "bg-transparent text-zinc-400 border-zinc-800 hover:border-amber-700/50"
                  : "bg-transparent text-zinc-700 border-zinc-900 cursor-not-allowed"
              }`}
            >
              Round {round}
            </button>
          );
        })}
      </div>
      
      {/* Results */}
      {loading ? (
        <Loader label="Loading fights…" />
      ) : !results ? (
        <Empty label={`Round ${selectedRound} fights have not been executed yet.`} />
      ) : (
        <div className="space-y-4">
          {/* Round Summary */}
          <div className="border border-amber-900/30 bg-zinc-950/50 p-5 rounded-sm">
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2 font-manrope">
              Alliance Wars Round {results.round}
            </div>
            <div className="font-cinzel text-base text-amber-400 uppercase tracking-wide">
              {totalFights} Fights Completed
            </div>
            <div className="text-xs text-zinc-500 mt-2 font-manrope">
              Executed: {new Date(results.executed_at).toLocaleString()}
            </div>
            {totalPages > 1 && (
              <div className="text-xs text-zinc-600 mt-1 font-manrope">
                Showing {startIndex + 1}-{Math.min(endIndex, totalFights)} of {totalFights}
              </div>
            )}
          </div>
          
          {/* Fights */}
          <div className="space-y-2">
            {paginatedFights.map((fight, idx) => (
              <FightCard key={idx} fight={fight} />
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center pt-4">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FightCard({ fight }) {
  const [expanded, setExpanded] = useState(false);
  const [showBattle, setShowBattle] = useState(false);
  
  const g1Won = fight.winner_id === fight.g1_id;
  const g2Won = fight.winner_id === fight.g2_id;
  
  // Build battle object for BattleReplay component
  const battleData = fight.commentary && fight.commentary.length > 0 ? {
    log: fight.commentary,  // BattleReplay expects "log" not "commentary"
    hp_history: fight.hp_history || [],
    g1_id: fight.g1_id,
    g1: fight.g1_name,
    g1_class: fight.g1_class,
    g2_id: fight.g2_id,
    g2: fight.g2_name,
    g2_class: fight.g2_class,
    winner_id: fight.winner_id,
    rounds: fight.rounds || 0
  } : null;
  
  return (
    <>
      <div className="border border-zinc-800 bg-zinc-950/50 overflow-hidden rounded-sm">
        <div
          className="p-4 cursor-pointer hover:bg-zinc-900/20 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Link 
                  to={`/gladiator/${fight.g1_id}`}
                  className={`text-sm font-cinzel uppercase tracking-wide hover:text-amber-400 transition-colors ${g1Won ? "text-green-400" : "text-zinc-400"}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {fight.g1_name}
                </Link>
                {g1Won && (
                  <span className="text-[10px] font-bold text-green-400 bg-green-950/30 px-1.5 py-0.5 rounded uppercase tracking-wider">
                    WIN
                  </span>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-manrope mt-0.5">{fight.g1_stance || "balanced"}</div>
              {fight.g1_owner_name && (
                <div className="text-[10px] text-zinc-600 font-manrope mt-0.5">
                  <span className="text-zinc-700">Ludus: </span>
                  <Link 
                    to={`/profile/${fight.g1_owner_id}`}
                    className="text-zinc-500 hover:text-amber-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {fight.g1_owner_name}
                  </Link>
                </div>
              )}
              {fight.g1_alliance_name && (
                <div className="text-[10px] text-zinc-600 font-manrope mt-0.5">
                  <span className="text-zinc-700">Alliance: </span>
                  <Link 
                    to={`/alliance/${fight.g1_alliance_id}`}
                    className="text-zinc-500 hover:text-amber-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {fight.g1_alliance_name}
                  </Link>
                </div>
              )}
            </div>
            
            <div className="flex flex-col items-center gap-1">
              <div className="text-amber-600 font-cinzel text-xs uppercase tracking-wider">VS</div>
              {fight.rounds > 0 && (
                <div className="text-[10px] text-zinc-500 font-manrope">
                  {fight.rounds} {fight.rounds === 1 ? 'round' : 'rounds'}
                </div>
              )}
            </div>
            
            <div className="flex-1 text-right">
              <div className="flex items-center justify-end gap-2">
                {g2Won && (
                  <span className="text-[10px] font-bold text-green-400 bg-green-950/30 px-1.5 py-0.5 rounded uppercase tracking-wider">
                    WIN
                  </span>
                )}
                <Link 
                  to={`/gladiator/${fight.g2_id}`}
                  className={`text-sm font-cinzel uppercase tracking-wide hover:text-amber-400 transition-colors ${g2Won ? "text-green-400" : "text-zinc-400"}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {fight.g2_name}
                </Link>
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-manrope mt-0.5">{fight.g2_stance || "balanced"}</div>
              {fight.g2_owner_name && (
                <div className="text-[10px] text-zinc-600 font-manrope mt-0.5">
                  <span className="text-zinc-700">Ludus: </span>
                  <Link 
                    to={`/profile/${fight.g2_owner_id}`}
                    className="text-zinc-500 hover:text-amber-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {fight.g2_owner_name}
                  </Link>
                </div>
              )}
              {fight.g2_alliance_name && (
                <div className="text-[10px] text-zinc-600 font-manrope mt-0.5">
                  <span className="text-zinc-700">Alliance: </span>
                  <Link 
                    to={`/alliance/${fight.g2_alliance_id}`}
                    className="text-zinc-500 hover:text-amber-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {fight.g2_alliance_name}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {expanded && (
          <div className="border-t border-zinc-800 bg-zinc-900/30 p-4">
            {battleData ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBattle(true);
                }}
                className="w-full px-4 py-2 bg-amber-900/20 hover:bg-amber-900/30 border border-amber-700/50 rounded text-amber-400 text-sm font-cinzel uppercase tracking-wide transition-colors"
              >
                View Full Battle Replay
              </button>
            ) : (
              <div className="text-xs text-zinc-500 text-center font-manrope">
                Battle commentary not available for this fight
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Battle Replay Modal */}
      {showBattle && battleData && (
        <BattleReplay
          battle={battleData}
          open={showBattle}
          onClose={() => setShowBattle(false)}
          shareable={false}
        />
      )}
    </>
  );
}

